import React, { FunctionComponent, useRef, useEffect } from 'react';

import { css } from '@emotion/react';

import Button from '@components/Common/Button';

import { useOnClickOutside } from '@hooks/useOnClickOutside';

import { palette } from '@styles/theme';

import ModalPortal from './ModalPortal';

interface Props {
  open: boolean;
  handleOpen: () => void;
  title: string;
  buttonText?: [string, string];
  handleConfirm: any;
  handleCancel: () => void;
}

const ConfirmModal: FunctionComponent<Props> = ({
  open,
  handleOpen,
  title,
  buttonText,
  handleConfirm,
  handleCancel,
}) => {
  const ref = useRef(null);

  useOnClickOutside(ref, () => handleOpen());

  useEffect(() => {
    const escKeyPress = (e: any) => {
      if (e.key === 'Escape') handleOpen();
    };
    window.addEventListener('keydown', escKeyPress);
    return () => window.removeEventListener('keydown', escKeyPress);
  }, []);

  return (
    <ModalPortal>
      <div css={[container, open && openModal]}>
        <div css={modal} ref={ref}>
          <div css={header}>{title}</div>
          <div css={contents}>
            <Button
              label={buttonText ? buttonText[0] : '취소하기'}
              theme="grayscale"
              handleClick={handleCancel}
              height={40}
            />
            <Button
              label={buttonText ? buttonText[1] : '등록하기'}
              theme="secondary"
              handleClick={handleConfirm}
              height={40}
            />
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ConfirmModal;

const container = css`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.4);

  @keyframes modal-bg-show {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const openModal = css`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: modal-bg-show 0.3s;
`;

const modal = css`
  position: relative;
  width: 300px;
  height: auto;
  background-color: #ffffff;
  box-shadow: 0px 5px 16px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
`;

const header = css`
  display: flex;
  position: relative;
  min-height: 72px;
  max-height: 94px;
  justify-content: flex-start;
  padding: 20px;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: #222;
  border-bottom: 1px solid ${palette.gray3};
`;

const contents = css`
  display: flex;
  padding: 20px;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
`;
